/**
 * VÉRAULT Member Suite — CuratedGuideOverlay (Wave 28.1 · Phase 5d).
 * ────────────────────────────────────────────────────────────────────
 * Editorial mini-journal overlay. Surfaces a Curated Guide at full
 * screen with cinematic typography and pacing — never a blog page.
 *
 *   ESC key to close · backdrop blur · serif-only reading column
 *   with a quiet pull-quote at the center.
 */
import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Quote, Clock } from "lucide-react";

export default function CuratedGuideOverlay({ guide, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!guide) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md overflow-y-auto"
        onClick={onClose}
        data-testid="curated-guide-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl mx-auto my-6 sm:my-10 rounded-lg overflow-hidden bg-[hsl(var(--background))] shadow-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close guide"
            className="absolute top-3 right-3 z-10 inline-flex items-center justify-center size-9 rounded-full bg-white/85 backdrop-blur border border-[rgba(0,0,0,0.06)] hover:bg-white"
            data-testid="curated-guide-overlay-close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>

          {/* Hero */}
          <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
            <img
              src={guide.hero_image}
              alt={guide.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 text-white">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] opacity-80">
                Curated guide
              </p>
              <h1 className="mt-2 font-serif text-[28px] sm:text-[40px] leading-[1.05] tracking-tight">
                {guide.title}
              </h1>
              <p className="mt-3 font-accent italic text-[15px] sm:text-[18px] opacity-90 max-w-xl">
                {guide.subtitle}
              </p>
              <div className="mt-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">
                <Clock className="size-3" strokeWidth={1.5} />
                {guide.read_minutes} min read
                {guide.tags?.length > 0 && (
                  <>
                    <span>·</span>
                    <span>{guide.tags.join(" · ")}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Reading column */}
          <article className="px-6 sm:px-12 py-10 sm:py-14 max-w-[640px] mx-auto">
            {guide.passages?.slice(0, 1).map((p, i) => (
              <p key={i} className="font-serif text-[17px] sm:text-[19px] leading-[1.7] text-[hsl(var(--foreground))] mb-7" data-testid={`guide-passage-${i}`}>
                {p}
              </p>
            ))}

            {/* Pull quote */}
            {guide.pull_quote && (
              <blockquote className="my-10 sm:my-14 text-center" data-testid="guide-pull-quote">
                <Quote className="size-5 text-[hsl(var(--accent-gold))] mx-auto mb-3" strokeWidth={1.2} />
                <p className="font-serif italic text-[20px] sm:text-[24px] leading-[1.4] text-[hsl(var(--foreground))] tracking-tight">
                  "{guide.pull_quote}"
                </p>
                <div className="mt-4 mx-auto w-12 h-px bg-[hsl(var(--accent-gold)/0.5)]" />
              </blockquote>
            )}

            {guide.passages?.slice(1).map((p, i) => (
              <p key={i + 1} className="font-serif text-[17px] sm:text-[19px] leading-[1.7] text-[hsl(var(--foreground))] mb-7" data-testid={`guide-passage-${i + 1}`}>
                {p}
              </p>
            ))}

            <div className="mt-12 pt-8 border-t border-[rgba(0,0,0,0.05)] text-center">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
                Curated by VÉRAULT Travels
              </p>
            </div>
          </article>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
